import { Flex } from '@chakra-ui/react';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { getGenesAndCopyNumbers } from '@resistapp/client/data-utils/plot-data/process-overview-line-datum';
import { useContainerWidth } from '@resistapp/client/hooks/use-container-width';
import { getMetricColor } from '@resistapp/client/utils/metric-utils';
import { metricScales } from '@resistapp/client/utils/overview-chart-configurations';
import { L1Target, L1Targets, L2TargetOrAssay, L2Targets, l2TargetsByL1 } from '@resistapp/common/assays';
import { friendlyCopyNumber, friendlyL2Target } from '@resistapp/common/friendly';
import { LOQ_COPY_NUMBER_IN_L } from '@resistapp/common/statistics/fold-change';
import { AbundanceMode, MetricMode, ProcessMode } from '@resistapp/common/types';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleBand, scaleLog } from '@visx/scale';
import { Line } from '@visx/shape';
import { useTooltipInPortal } from '@visx/tooltip';
import { chain, get } from 'lodash';
import { RefObject, useMemo } from 'react';
import { theme } from '../../../shared/theme';
import { PlotTooltip, usePlotTooltip } from '../../../tooltips/plot-tooltip';
import { TickLabel, useTickLabel } from '../../tick-label';
import { graphMargins } from '../consts';
import { friendlyGeneName } from '../site-details-utils';
import { getBarDatum } from './get-bar-datum';
import { ReductionBar } from './reduction-bar';
import { L2TargetTooltipContent } from './reduction-tooltip';
import { SingleBarDatum, TargetType } from './types';

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  height?: number;
  width?: number;
  selectedSiteDatum: OverviewDatum;
  groupingTarget: TargetType;
}

const margins = { vertical: 40, horizontal: 140 };
const yMarginForxAxisLegend = 5;
export const textPadding = 8;

export function TreatedAndReducedBarGraph({
  containerRef,
  height = 300,
  width = 0,
  selectedSiteDatum,
  groupingTarget,
}: Props) {
  const { metricMode, activeAbundanceMode, activeOverviewConfiguration } = useOverviewContext();
  const { getGroup, allGeneGroups, allAssays } = useAssayContext();
  const geneNameByAssay = useMemo(
    () =>
      chain(allAssays)
        .keyBy(a => a.assay)
        .mapValues(a => friendlyGeneName(a.assay, a.gene))
        .value(),
    [allAssays],
  );

  const analysedL2Targets = useMemo(
    () =>
      new Set(
        chain(selectedSiteDatum.beforeAbundances ?? [])
          .map(a => getGroup(a.assay, 'l2Target') as L2Targets | undefined)
          .filter((t): t is L2Targets => !!t)
          .value(),
      ),
    [selectedSiteDatum, getGroup],
  );

  const { queryFilters } = useSampleDataContext();
  const containerWidth = useContainerWidth(containerRef);
  const { hoveredRowTarget, hoveredTarget, setHoveredTarget, onMouseEnter, onMouseLeave } = useTickLabel();
  const isArg = (groupingTarget as L1Target) === L1Targets.ARG;

  const tooltipStuff = useTooltipInPortal({
    scroll: false,
    detectBounds: true,
  });
  const { tooltipData, tooltipProps } = usePlotTooltip<SingleBarDatum>(tooltipStuff);
  const tooltipRef = tooltipStuff.containerRef;

  if (!containerWidth) {
    return null;
  }

  const effectiveWidth = width || containerWidth - graphMargins;

  // Get data for both before and after samples
  const beforeGenesAndCopyNumbers = getGenesAndCopyNumbers(
    selectedSiteDatum,
    groupingTarget,
    metricMode,
    ProcessMode.BEFORE,
    getGroup,
    true,
  );
  const afterGenesAndCopyNumbers = getGenesAndCopyNumbers(
    selectedSiteDatum,
    groupingTarget,
    metricMode,
    ProcessMode.AFTER,
    getGroup,
    true,
  );

  // Get the list of targets to show based on the grouping target
  // This might include L2 targets or assays depending on grouping
  const targetList = get(
    l2TargetsByL1,
    groupingTarget,
    chain(beforeGenesAndCopyNumbers)
      .map(g => g.assay)
      .uniq()
      .value(),
  );

  // Get current selections
  const { selectedTargets } = queryFilters.filters;

  // Calculate totals for each target
  const barData = chain(targetList)
    .map(target =>
      getBarDatum(
        groupingTarget,
        target,
        beforeGenesAndCopyNumbers,
        afterGenesAndCopyNumbers,
        LOQ_COPY_NUMBER_IN_L,
        selectedSiteDatum,
      ),
    )

    .filter(datum => {
      const isAnalysedAntibiotic = analysedL2Targets.has(datum.barIdentifier as L2Targets); // Always show all analysed antibiotics
      const isSelected = selectedTargets.length === 1 && selectedTargets.includes(datum.barIdentifier as L2Targets); // Always show the selected gene, detected or not
      const isDetected = !!datum.beforeTotal || !!datum.afterTotal; // Always show all detected genes
      return isAnalysedAntibiotic || isDetected || isSelected;
    })
    .sort((a, b) => (b.afterTotal || 0) - (a.afterTotal || 0))
    .value();

  if (barData.length === 0 || activeAbundanceMode !== AbundanceMode.COPIES_PER_L) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%" width="100%">
        Data not available for site or sample type
      </Flex>
    );
  }

  const xMax = effectiveWidth - margins.horizontal;
  const yMax = height - margins.vertical;
  const treatedBarColor = getMetricColor(
    metricScales[MetricMode.REDUCTION][0],
    MetricMode.REDUCTION,
    activeAbundanceMode,
  );
  const increaseBarColor = getMetricColor(
    metricScales[MetricMode.REDUCTION][1],
    MetricMode.REDUCTION,
    activeAbundanceMode,
  );
  const reducedBarColor = getMetricColor(-2, MetricMode.REDUCTION, activeAbundanceMode);
  const lodColor = theme.colors.neutral700;

  const maxTotal = Math.max(...barData.map(d => Math.max(d.beforeTotal || 0, d.afterTotal || 0)));

  const yScale = scaleBand<L2Targets | string>({
    range: [0, yMax],
    round: true,
    domain: barData.map(d => d.barIdentifier),
    padding: 0.4,
  });

  const minValue = LOQ_COPY_NUMBER_IN_L / 10;
  const xScale = scaleLog<number>({
    range: [0, xMax],
    round: true,
    domain: [minValue, (maxTotal || LOQ_COPY_NUMBER_IN_L) * 18],
    base: 10,
  });

  const lodX = xScale(LOQ_COPY_NUMBER_IN_L);

  return (
    <>
      <PlotTooltip {...tooltipProps} backgroundColor={theme.colors.blue50}>
        {tooltipData && (
          <L2TargetTooltipContent
            tooltipData={tooltipData}
            activeOverviewConfiguration={activeOverviewConfiguration}
            allGeneGroups={allGeneGroups}
          />
        )}
      </PlotTooltip>
      <svg
        width={effectiveWidth}
        height={height + yMarginForxAxisLegend}
        overflow="visible"
        ref={tooltipRef}
        // zIndex required position. We use this to position the tooltip above other content
        style={{ zIndex: 10000, position: 'relative' }}
      >
        <Group top={0} left={margins.horizontal}>
          <GridColumns
            left={0}
            height={height - 34}
            scale={xScale}
            stroke={theme.colors.neutral300}
            tickValues={xScale.ticks(6).filter(value => Math.log10(value) % 1 === 0)}
          />
          {/* LOD line */}
          <Line
            from={{ x: lodX, y: 0 }}
            to={{ x: lodX, y: height - 34 }}
            stroke={lodColor}
            strokeOpacity={1}
            strokeWidth={2}
            strokeDasharray="2, 5"
          />
          <text
            x={lodX}
            y={yMax + 45}
            fontSize={12}
            fill={lodColor}
            fontWeight={theme.fontWeight.bold}
            textAnchor="middle"
          >
            (LOQ)
          </text>
          <AxisBottom
            scale={xScale}
            top={yMax}
            tickFormat={n => friendlyCopyNumber(n.valueOf())}
            tickValues={xScale.ticks(6).filter(value => Math.log10(value) % 1 === 0)}
            hideTicks
            hideAxisLine
            tickLabelProps={{
              fontSize: 14,
              color: theme.colors.neutral700,
              fontWeight: theme.fontWeight.bold,
            }}
          />
          {barData.map((data: SingleBarDatum) => {
            const barHeight = yScale.bandwidth();
            const barY = yScale(data.barIdentifier) || 0;
            // Handle zero values by using minValue
            const beforeWidth = xScale(data.beforeTotal || minValue);
            const afterWidth = xScale(data.afterTotal || minValue);

            return (
              <ReductionBar
                key={data.barIdentifier}
                datum={data}
                barY={barY}
                barHeight={barHeight}
                beforeWidth={data.isDecrease ? beforeWidth : afterWidth}
                afterWidth={data.isDecrease ? afterWidth : beforeWidth}
                baseBarColor={data.isDecrease ? treatedBarColor : increaseBarColor}
                changeBarColor={data.isDecrease ? reducedBarColor : increaseBarColor}
                tooltipStuff={tooltipStuff}
                hoveredTarget={hoveredTarget as L2TargetOrAssay | null}
                setHoveredTarget={setHoveredTarget}
                xScale={xScale}
              />
            );
          })}
          <g onMouseLeave={onMouseLeave}>
            {/* Invisible rectangle to capture mouse leave events */}
            <rect
              x={-margins.horizontal}
              y={0}
              width={margins.horizontal}
              height={yMax}
              fill="transparent"
              style={{ pointerEvents: 'all' }}
            />
            <AxisLeft
              scale={yScale}
              tickFormat={value => {
                if ((groupingTarget as L1Target) === L1Targets.ARG) {
                  return friendlyL2Target(value as L2Targets);
                }
                const geneName = get(geneNameByAssay, value, value);
                const abreviated = geneName.replace('resistance', 'res.');
                return abreviated[abreviated.length - 2] === '_' ? abreviated.slice(0, -2) : abreviated;
              }}
              tickLabelProps={{
                fontSize: 14,
                color: theme.colors.neutral700,
                fontWeight: theme.fontWeight.bold,
                cursor: isArg ? 'pointer' : 'default',
                dy: '.33em',
                textAnchor: 'end',
                x: -10,
              }}
              tickComponent={({ x, y, formattedValue }) => {
                if (!formattedValue) return null;

                // Get the original identifier by finding which band contains this y position
                const originalIdentifier = yScale.domain().find((_, i) => {
                  const bandStart = yScale(yScale.domain()[i]);
                  const bandEnd = bandStart ? bandStart + yScale.bandwidth() : undefined;

                  return bandStart !== undefined && bandEnd !== undefined && y >= bandStart && y <= bandEnd;
                });

                // If we couldn't find the identifier, return null
                if (!originalIdentifier) return null;

                const isSelected =
                  queryFilters.filters.selectedTargets.length === 0 ||
                  queryFilters.filters.selectedTargets.includes(originalIdentifier as L2Targets);

                const isTheOnlySelected = isSelected && queryFilters.filters.selectedTargets.length === 1;

                // Find the corresponding gene data for the tooltip
                const geneData = barData.find(d => d.barIdentifier === originalIdentifier);

                return (
                  <TickLabel
                    x={x}
                    y={y}
                    formattedValue={formattedValue}
                    identifier={originalIdentifier as L2TargetOrAssay}
                    isSelected={isSelected}
                    isTheOnlySelected={isTheOnlySelected}
                    geneData={geneData}
                    groupingTarget={groupingTarget}
                    onMouseEnter={onMouseEnter}
                    hoveredTarget={hoveredTarget}
                    hoveredRowTarget={hoveredRowTarget}
                    onClick={() => {
                      queryFilters.toggleSingleTarget(originalIdentifier);
                    }}
                  />
                );
              }}
              hideTicks
              hideAxisLine
            />
          </g>
        </Group>
      </svg>
    </>
  );
}
